import type { LngLat } from "../adapters/types";

export interface AddressSearchResult {
  label: string;
  center: LngLat;
  bounds?: [LngLat, LngLat];
}

/** Délai (ms) avant de lancer la recherche après la dernière frappe — évite d'envoyer une requête à
 * Nominatim à chaque caractère tapé (leur politique d'usage attend un usage raisonnable, pas de
 * clé API/limite de débit fournie côté client pour l'appliquer nous-mêmes). */
const DEBOUNCE_MS = 400;

/** Nombre maximum de résultats demandés à Nominatim. */
const MAX_RESULTS = 5;

/** Isole le parsing de la réponse (pure, testable sans réseau) du `fetch` lui-même — une réponse
 * inattendue (Nominatim en panne, format changé) retombe silencieusement sur `[]` plutôt que de
 * faire planter la recherche. */
export function parseNominatimResults(json: unknown): AddressSearchResult[] {
  if (!Array.isArray(json)) return [];
  const results: AddressSearchResult[] = [];
  for (const item of json) {
    if (typeof item !== "object" || item === null) continue;
    const { lat, lon, display_name, boundingbox } = item as Record<string, unknown>;
    if (typeof lat !== "string" || typeof lon !== "string" || typeof display_name !== "string") continue;
    const center: LngLat = [Number(lon), Number(lat)];
    if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) continue;
    const box = Array.isArray(boundingbox) ? boundingbox.map(Number) : [];
    const [south, north, west, east] = box;
    const hasBounds = box.length === 4 && box.every(Number.isFinite);

    results.push({
      label: display_name,
      center,
      ...(hasBounds ? { bounds: [[west!, south!], [east!, north!]] as [LngLat, LngLat] } : {}),
    });
  }
  return results;
}

/** Sépare `AddressSearchResult.label` (le `display_name` brut de Nominatim, ex. "Tour Eiffel, 5,
 * Avenue Anatole France, Paris, France") en un titre (1er segment, le nom/numéro le plus
 * spécifique) et un sous-titre (le reste, le contexte géographique) — même présentation à 2 lignes
 * que `SearchBox`'s titre/sous-titre, pour rester visuellement cohérent avec l'autre champ de
 * recherche du produit plutôt que d'afficher la chaîne brute d'un seul bloc. */
export function splitLabel(label: string): { title: string; subtitle: string | null } {
  const commaIndex = label.indexOf(",");
  if (commaIndex === -1) return { title: label, subtitle: null };
  return { title: label.slice(0, commaIndex), subtitle: label.slice(commaIndex + 1).trim() };
}

export async function geocodeAddress(query: string, signal: AbortSignal): Promise<AddressSearchResult[]> {
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=${MAX_RESULTS}&q=${encodeURIComponent(query)}`;
  const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) return [];
  return parseNominatimResults(await response.json());
}

/**
 * Champ de recherche d'adresse flottant, affiché en haut de l'écran UNIQUEMENT pendant l'édition
 * interactive de la vue par défaut (voir `MapLibreAdapter.startEditingMapView`/`createTokaMap.ts`'s
 * `"map-view:edit-started"`/`"map-view:edit-ended"`) — contrairement à `SearchBox` (qui filtre une
 * liste locale reconstruite depuis `ShapeStore`), interroge Nominatim (OpenStreetMap) à chaque frappe
 * (débattue, voir `DEBOUNCE_MS`) pour géocoder une adresse tapée en clair. `onSelect` reçoit les
 * coordonnées résolues ; c'est `createTokaMap.ts` qui décide quoi en faire (recentrer la carte via
 * `mapAdapter.setCenter` ET zoomer via `mapAdapter.setZoom`, voir `ADDRESS_SEARCH_ZOOM`, pour que la
 * validation amène réellement sur le point visé).
 */
export class AddressSearchBox {
  private element: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private clearButton: HTMLButtonElement | null = null;
  private iconEl: SVGSVGElement | null = null;
  private resultsEl: HTMLDivElement | null = null;
  private activeIndex = -1;
  private currentResults: AddressSearchResult[] = [];
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private abortController: AbortController | null = null;
  /** Distingue "recherche jamais lancée" (dropdown fermée) de "recherche terminée sans résultat"
   * (message "Aucun résultat" — voir `renderResults`), sans quoi les deux se confondraient : toutes
   * deux partent d'un `currentResults` vide. */
  private searched = false;

  constructor(
    private container: HTMLElement,
    private onSelect: (result: AddressSearchResult) => void,
  ) {}

  show(): void {
    if (this.element) return;

    this.element = document.createElement("div");
    this.element.className = "toka-map-view-address-search";

    const inputWrapper = document.createElement("div");
    inputWrapper.className = "toka-map-view-address-search__input-wrapper";

    const icon = document.createElement("span");
    icon.className = "toka-map-view-address-search__icon";
    icon.setAttribute("aria-hidden", "true");
    this.iconEl = createPinIcon();
    icon.appendChild(this.iconEl);
    inputWrapper.appendChild(icon);

    const input = document.createElement("input");
    input.type = "text";
    input.className = "toka-map-view-address-search__input";
    input.placeholder = "Chercher une adresse…";
    input.setAttribute("aria-label", "Chercher une adresse");
    input.addEventListener("input", this.handleInput);
    input.addEventListener("keydown", this.handleKeyDown);
    inputWrapper.appendChild(input);
    this.input = input;

    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = "toka-map-view-address-search__clear";
    clearButton.textContent = "✕";
    clearButton.setAttribute("aria-label", "Effacer la recherche");
    clearButton.addEventListener("click", () => {
      input.value = "";
      input.focus();
      this.updateClearButtonVisibility();
      this.abortController?.abort();
      if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.searched = false;
      this.setLoading(false);
      this.renderResults([]);
    });
    inputWrapper.appendChild(clearButton);
    this.clearButton = clearButton;
    this.updateClearButtonVisibility();

    this.element.appendChild(inputWrapper);

    const resultsEl = document.createElement("div");
    resultsEl.className = "toka-map-view-address-search__results";
    this.element.appendChild(resultsEl);
    this.resultsEl = resultsEl;

    this.container.appendChild(this.element);
    document.addEventListener("mousedown", this.handleOutsideClick);
    input.focus();
  }

  hide(): void {
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.abortController?.abort();
    this.abortController = null;
    document.removeEventListener("mousedown", this.handleOutsideClick);
    this.element?.remove();
    this.element = null;
    this.input = null;
    this.clearButton = null;
    this.iconEl = null;
    this.resultsEl = null;
    this.currentResults = [];
    this.activeIndex = -1;
    this.searched = false;
  }

  private updateClearButtonVisibility(): void {
    if (this.clearButton) this.clearButton.style.display = this.input?.value ? "" : "none";
  }

  private setLoading(loading: boolean): void {
    this.iconEl?.classList.toggle("toka-map-view-address-search__icon-svg--loading", loading);
  }

  private handleInput = (): void => {
    this.updateClearButtonVisibility();
    if (this.debounceTimer !== null) clearTimeout(this.debounceTimer);
    const query = this.input?.value.trim() ?? "";
    if (!query) {
      this.abortController?.abort();
      this.setLoading(false);
      this.searched = false;
      this.renderResults([]);
      return;
    }
    this.debounceTimer = setTimeout(() => this.runSearch(query), DEBOUNCE_MS);
  };

  private async runSearch(query: string): Promise<void> {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoading(true);
    let results: AddressSearchResult[] = [];
    try {
      results = await geocodeAddress(query, controller.signal);
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("[AddressSearchBox] échec de la recherche d'adresse :", error);
    }
    if (controller.signal.aborted) return;
    this.setLoading(false);
    this.searched = true;
    this.renderResults(results);
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.renderResults([]);
      return;
    }
    if (!this.currentResults.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.setActiveIndex((this.activeIndex + 1) % this.currentResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.setActiveIndex((this.activeIndex - 1 + this.currentResults.length) % this.currentResults.length);
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault();
      this.selectResult(this.currentResults[this.activeIndex]!);
    }
  };

  private handleOutsideClick = (event: MouseEvent): void => {
    if (event.target instanceof Node && this.element?.contains(event.target)) return;
    this.renderResults([]);
  };

  private setActiveIndex(index: number): void {
    this.activeIndex = index;
    const rows = this.resultsEl?.querySelectorAll<HTMLButtonElement>(".toka-map-view-address-search__result");
    rows?.forEach((row, rowIndex) => {
      row.classList.toggle("toka-map-view-address-search__result--active", rowIndex === index);
    });
    rows?.[index]?.scrollIntoView({ block: "nearest" });
  }

  private selectResult(result: AddressSearchResult): void {
    if (this.input) this.input.value = result.label;
    this.updateClearButtonVisibility();
    this.searched = false;
    this.renderResults([]);
    this.onSelect(result);
  }

  private renderResults(results: AddressSearchResult[]): void {
    this.currentResults = results;
    this.activeIndex = -1;
    if (!this.resultsEl) return;
    this.resultsEl.innerHTML = "";

    if (!results.length) {
      // "Aucun résultat" seulement après une VRAIE recherche terminée (`searched`) — pas pour un
      // champ vide/juste effacé, ni pendant le débat (`handleInput` remet `searched` à `false`
      // avant même de programmer `runSearch`).
      if (this.searched) {
        this.resultsEl.style.display = "flex";
        const empty = document.createElement("div");
        empty.className = "toka-map-view-address-search__empty";
        empty.textContent = "Aucun résultat";
        this.resultsEl.appendChild(empty);
      } else {
        this.resultsEl.style.display = "none";
      }
      return;
    }
    this.resultsEl.style.display = "flex";

    for (const result of results) {
      const { title, subtitle } = splitLabel(result.label);
      const row = document.createElement("button");
      row.type = "button";
      row.className = "toka-map-view-address-search__result";

      const icon = document.createElement("span");
      icon.className = "toka-map-view-address-search__result-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.appendChild(createPinIcon());
      row.appendChild(icon);

      const text = document.createElement("span");
      text.className = "toka-map-view-address-search__result-text";
      const titleEl = document.createElement("span");
      titleEl.className = "toka-map-view-address-search__result-title";
      titleEl.textContent = title;
      text.appendChild(titleEl);
      if (subtitle) {
        const subtitleEl = document.createElement("span");
        subtitleEl.className = "toka-map-view-address-search__result-subtitle";
        subtitleEl.textContent = subtitle;
        text.appendChild(subtitleEl);
      }
      row.appendChild(text);

      // `mousedown` (pas `click`) : se déclenche avant le `blur`/`focus`-out de l'input, qui
      // fermerait sinon la liste (voir `handleOutsideClick`) avant que le clic n'atteigne cette ligne.
      row.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.selectResult(result);
      });
      this.resultsEl.appendChild(row);
    }

    this.setActiveIndex(0);
  }
}

function createPinIcon(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.classList.add("toka-map-view-address-search__icon-svg");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M12 21s-7-6.1-7-11.5A7 7 0 0 1 19 9.5C19 14.9 12 21 12 21Z");
  const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "9.5");
  circle.setAttribute("r", "2.25");
  svg.appendChild(path);
  svg.appendChild(circle);
  return svg;
}
